import {
  type DecisionAnswerConflict,
  type DecisionAnswerView,
  type DecisionImportIssue,
} from '@apm/contracts';
import { ApiRequestError } from '../../lib/http';

/**
 * Đọc lỗi có mã của Sổ chốt phương án — **chỗ duy nhất** biết hình dạng
 * `details` mà module API `decision-sheets` trả (docs/api-conventions.md §3).
 *
 * Web và API được viết song song (APM-DSHEET-a ∥ c/d), nên tên trường còn có
 * thể lệch lúc gộp. Mọi component đi qua ba hàm dưới đây; lệch thì sửa ở đây,
 * không đi dò từng chỗ.
 *
 * Mã có thể tới theo hai đường mà repo đều đang dùng: `error.code` là chính
 * mã ấy (thêm vào `ErrorCode`), hoặc mã chung (`CONFLICT`, `FORBIDDEN`,
 * `VALIDATION_FAILED`) kèm `details.reason`. Đọc cả hai.
 */

export type DecisionErrorCode =
  | 'DECISION_ANSWER_CONFLICT'
  | 'DECISION_IMPORT_INVALID'
  | 'DECISION_SHEET_NOT_READY'
  | 'DECISION_SHEET_CLOSED'
  | 'DECISION_SHEET_NOT_CLOSED'
  | 'DECISION_NOT_DECIDER'
  | 'DECISION_OPTION_IN_USE'
  | 'DECISION_QUESTION_ANSWERED'
  | 'DECISION_DECIDER_NO_ACCESS'
  | 'DECISION_SHEET_NOT_YET'
  | 'DECISION_SUGGESTION_REVIEWED'
  | 'DECISION_SUGGESTION_UNUSABLE'
  // Link công khai — APM-DSX-b, plan §11.3.
  | 'DECISION_SHARE_UNAVAILABLE'
  | 'DECISION_SHARE_READ_ONLY'
  | 'DECISION_SHARE_RATE_LIMITED'
  | 'DECISION_RESPONDENT_REQUIRED';

export function decisionErrorCode(error: unknown): DecisionErrorCode | null {
  if (!(error instanceof ApiRequestError)) return null;
  const code = String(error.code);
  if (code.startsWith('DECISION_')) return code as DecisionErrorCode;
  const reason = error.details?.['reason'];
  return typeof reason === 'string' && reason.startsWith('DECISION_')
    ? (reason as DecisionErrorCode)
    : null;
}

/**
 * 409 `DECISION_ANSWER_CONFLICT` — đáp án hiện tại của câu (plan §4.3).
 *
 * Nhận cả `details` phẳng (`{questionId, answer, answerRev}`) lẫn bọc một lớp
 * (`details.current`) — chưa biết API chọn cách nào. Thiếu `answerRev` thì
 * trả `null`: không có rev thì không gửi lại được, và đoán rev là ghi đè im
 * lặng đúng thứ khoá lạc quan sinh ra để chặn.
 */
/** Lý do sổ chưa mở được — `409 DECISION_SHEET_NOT_READY`, chi tiết do server tính. */
export interface SheetNotReady {
  readonly missingQuestions: boolean;
  /** Không người chốt nào **và** không link "Trả lời" nào còn hiệu lực. */
  readonly missingAnswerers: boolean;
}

export function readNotReady(error: unknown): SheetNotReady | null {
  if (decisionErrorCode(error) !== 'DECISION_SHEET_NOT_READY') return null;
  const details = (error as ApiRequestError).details ?? {};
  return {
    missingQuestions: details['missingQuestions'] === true,
    missingAnswerers: details['missingDeciders'] === true,
  };
}

/**
 * `409 DECISION_SHEET_NOT_CLOSED` — xuất thành tài liệu dự án khi sổ chưa đóng
 * (APM-DSX). Trạng thái lúc server từ chối: `open` | `draft`.
 */
export function readNotClosed(error: unknown): 'open' | 'draft' | null {
  if (decisionErrorCode(error) !== 'DECISION_SHEET_NOT_CLOSED') return null;
  return (error as ApiRequestError).details?.['status'] === 'draft' ? 'draft' : 'open';
}

export function readAnswerConflict(error: unknown): DecisionAnswerConflict | null {
  if (decisionErrorCode(error) !== 'DECISION_ANSWER_CONFLICT') return null;
  const details = (error as ApiRequestError).details ?? {};
  const nested = details['current'];
  const source =
    typeof nested === 'object' && nested !== null ? (nested as Record<string, unknown>) : details;
  const answerRev = source['answerRev'];
  if (typeof answerRev !== 'number') return null;
  const answer = source['answer'];
  return {
    questionId: typeof source['questionId'] === 'string' ? source['questionId'] : '',
    answer: isAnswer(answer) ? answer : null,
    answerRev,
  };
}

/** 400 `DECISION_IMPORT_INVALID` — danh sách lỗi theo đường dẫn (plan §5.2). */
export function readImportIssues(error: unknown): DecisionImportIssue[] | null {
  if (decisionErrorCode(error) !== 'DECISION_IMPORT_INVALID') return null;
  const issues = (error as ApiRequestError).details?.['issues'];
  if (!Array.isArray(issues)) return null;
  return issues.filter(
    (issue): issue is DecisionImportIssue =>
      typeof issue === 'object' &&
      issue !== null &&
      typeof (issue as DecisionImportIssue).path === 'string' &&
      typeof (issue as DecisionImportIssue).message === 'string',
  );
}

function isAnswer(value: unknown): value is DecisionAnswerView {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as DecisionAnswerView).key === 'string' &&
    typeof (value as DecisionAnswerView).answeredAt === 'string'
  );
}
